import { useState, useEffect } from 'react';
import './App.css';
import './Demo.css';
import { apiUrl } from './config';
import { SOAPNoteDisplay } from './components/soap/SOAPNoteDisplay';
import { FormattedText } from './components/common/FormattedText';
import {
  trackDemoVisit,
  trackGenerateNoteClick,
  setupTimeOnPageTracking,
  trackClickBookCallFromDemo
} from './utils/analytics';

function Demo() {
  // 3 demo patients, each with their own audio file
  const demoPatients = [
    {
      id: 1,
      full_name: 'Sarah Mitchell',
      email: 'sarah@example.com',
      dob: '1988-03-15',
      audioFile: '/test-audio/dental_term.mp3'
    },
    {
      id: 2,
      full_name: 'James Okonkwo',
      email: 'james@example.com',
      dob: '1975-11-22',
      audioFile: '/test-audio/DentalRecording.m4a'
    },
    {
      id: 3,
      full_name: 'Lisa Tran',
      email: 'lisa@example.com',
      dob: '2001-06-09',
      audioFile: '/test-audio/non_dental_audio.mp3'
    }
  ];

  // States
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [soapNote, setSoapNote] = useState(null);
  const [error, setError] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [copied, setCopied] = useState(false);
  const [expandedNote, setExpandedNote] = useState(false);
  const [activeTab, setActiveTab] = useState('soap'); // 'overview' or 'soap'

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  // Track demo page visit on mount
  useEffect(() => {
    trackDemoVisit();
    const cleanupTimeTracking = setupTimeOnPageTracking('demo');
    return () => {
      cleanupTimeTracking();
    };
  }, []);

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSoapNote(null);
    setError(null);
  };

  const handleGenerateSOAP = async () => {
    // Validate inputs
    if (!selectedPatient) {
      setError('Please select a patient before generating a SOAP note.');
      return;
    }
    if (!appointmentDate || !/^\d{4}-\d{2}-\d{2}$/.test(appointmentDate)) {
      setError('Please select a valid appointment date.');
      return;
    }

    trackGenerateNoteClick();

    setIsGenerating(true);
    setError(null);
    setSoapNote(null);
    setElapsedTime(0);

    // Start stopwatch
    const id = setInterval(() => {
      setElapsedTime((prevTime) => prevTime + 0.1);
    }, 100);

    try {
      // Fetch the audio file for the selected patient
      const audioResponse = await fetch(selectedPatient.audioFile);
      const audioBlob = await audioResponse.blob();

      const formData = new FormData();
      formData.append('file', audioBlob, selectedPatient.audioFile.split('/').pop());

      const response = await fetch(apiUrl('/api/demo/transcribe'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.insufficient_content) {
        setError('The audio does not contain sufficient dental clinical information.');
        setSoapNote(null);
      } else {
        setSoapNote(data);
      }

    } catch (err) {
      console.error('Error generating SOAP note:', err);
      setError(err.message || 'Failed to generate SOAP note. Please try again.');
    } finally {
      if (id) {
        clearInterval(id);
      }
      setIsGenerating(false);
    }
  };

  const formatTime = (seconds) => {
    return seconds.toFixed(1) + 's';
  };

  const handleBookCallFromDemo = () => {
    trackClickBookCallFromDemo();
    window.Calendly.initPopupWidget({ url: 'https://calendly.com/gabikir1999/30min' });
    return false;
  };

  const handleCloseSoapNote = () => {
    setSoapNote(null);
    setError(null);
  };

  const handleCopySOAP = async () => {
    if (!soapNote || !soapNote.soap_note) return;

    const noteToUse = soapNote.soap_note;
    const { subjective, objective, assessment, plan } = noteToUse;

    // Build header with patient info
    let header = 'SOAP NOTE';
    if (selectedPatient?.full_name) {
      header += `\nPatient: ${selectedPatient.full_name}`;
    }
    if (appointmentDate) {
      const [year, month, day] = appointmentDate.split('-');
      const formattedDate = new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      header += `\nDate: ${formattedDate}`;
    }
    header += '\n';

    let soapText = `${header}

SUBJECTIVE
${subjective || 'N/A'}

OBJECTIVE
${objective || 'N/A'}

ASSESSMENT
${assessment || 'N/A'}

PLAN
${plan || 'N/A'}`;

    try {
      await navigator.clipboard.writeText(soapText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // No-op handlers for demo mode
  const handleEditSOAP = () => {};
  const saveToHistory = () => {};

  // No-op handlers for SOAPNoteDisplay (demo doesn't support some features)
  const noOp = () => {};

  return (
    <div className="App">
      <div className="container app-page-container">
        {/* Header Section - mirrors App.js */}
        <div className="header-section">
          <div className="header-top">
            {/* Logo + Title */}
            <div className="logo-wrapper">
              <div className="logo-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <path d="M24 2L36 12V22C36 32 24 46 24 46C24 46 12 32 12 22V12L24 2Z" fill="white" />
                  <line x1="20" y1="14" x2="20" y2="26" stroke="#0b1224" strokeWidth="2" />
                  <line x1="24" y1="10" x2="24" y2="26" stroke="#0b1224" strokeWidth="2" />
                  <line x1="28" y1="14" x2="28" y2="26" stroke="#0b1224" strokeWidth="2" />
                </svg>
              </div>
              <h1 className="title">Algodentra</h1>
            </div>

            {/* Right Side: Home + Book Call + Profile Chip */}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <a href="/" style={{ textDecoration: 'none' }}>
                <button className="trial-cta-button" style={{ background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.3)', cursor: 'pointer' }}>
                  Home
                </button>
              </a>
              <button className="trial-cta-button" onClick={handleBookCallFromDemo}>
                Book a Call
              </button>

              {/* Profile Menu - simplified, not interactive */}
              <div className="profile-menu-button demo-profile-button">
                <div className="profile-avatar">A</div>
                <span className="profile-greeting">Welcome back Dr. Algodentra</span>
              </div>
            </div>
          </div>

          <p className="subtitle">AI-Powered SOAP Note Generator</p>
          <div className="divider"></div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Portal Hero Card */}
          <div className="portal-hero">
            <div className="portal-hero-left">
              <div className="portal-copy">
                <p className="portal-eyebrow">Dentist Portal</p>
                <h2 className="portal-title">Generate secure SOAP notes</h2>
                <p className="portal-subtitle">
                  Search a patient, set the appointment, then record or upload to keep
                  billing, history, and compliance aligned.
                </p>
              </div>
            </div>
            <div className="portal-hero-right">
              <div className="portal-patient-chip">
                <span className="portal-chip-label">Active Patient</span>
                <strong>{selectedPatient?.full_name || 'Not selected'}</strong>
              </div>
            </div>
          </div>

          {/* Patient Panel - only show if no SOAP note */}
          {!soapNote && (
            <div className="patient-panel">
              {/* Patient Select Dropdown */}
              <div className="patient-field">
                <label className="field-label" htmlFor="patientSelect">
                  Select Patient
                </label>
                <select
                  id="patientSelect"
                  className="field-input"
                  value={selectedPatient ? selectedPatient.id : ''}
                  onChange={(e) => {
                    const patient = demoPatients.find(p => p.id === parseInt(e.target.value));
                    handlePatientSelect(patient);
                  }}
                >
                  <option value="">-- Choose a patient --</option>
                  {demoPatients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Appointment Date Field */}
              <div className="patient-field">
                <label className="field-label" htmlFor="appointmentDate">
                  Appointment Date
                </label>
                <input
                  id="appointmentDate"
                  className="field-input"
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  max={today}
                />
              </div>
            </div>
          )}

          {/* Audio Player and Generate Button - only show if patient selected and no SOAP */}
          {selectedPatient && !soapNote && (
            <>
              <div className="audio-player-container">
                <div className="audio-player-header">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 19V5m0 0a3 3 0 016 0v14m-6-14a3 3 0 106 0" />
                  </svg>
                  <div className="audio-info">
                    <h3>Demo Audio</h3>
                    <p>{selectedPatient.audioFile.split('/').pop()}</p>
                  </div>
                </div>
                <audio key={selectedPatient.id} controls className="audio-player">
                  <source src={selectedPatient.audioFile} />
                  Your browser does not support the audio element.
                </audio>
              </div>

              {!appointmentDate && (
                <div className="date-required-message">
                  ⚠️ Please select an appointment date to generate a SOAP note
                </div>
              )}

              {appointmentDate && (
                <>
                  <button
                    className="action-btn primary-btn"
                    onClick={handleGenerateSOAP}
                    disabled={isGenerating}
                  >
                    {isGenerating ? 'Generating...' : 'Generate SOAP Note'}
                  </button>
                </>
              )}

              {isGenerating && (
                <div className="stopwatch-display">
                  <div className="stopwatch-icon">Timer</div>
                  <div className="stopwatch-time">{formatTime(elapsedTime)}</div>
                  <div className="stopwatch-label">Processing...</div>
                </div>
              )}
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Demo Mode Notice - Always visible when SOAP note is generated */}
          {soapNote && (
            <div className="demo-mode-banner">
              <p>Editing and saving to history are unavailable in demo mode. Sign up to access full features.</p>
            </div>
          )}

          {/* Patient Page View Button */}
          {soapNote && (
            <button
              className="view-patient-page-btn"
              onClick={() => setExpandedNote(!expandedNote)}
            >
              {expandedNote ? 'Hide Patient Page View' : 'View Patient Page'}
            </button>
          )}

          {/* Patient Portal Style View */}
          {soapNote && expandedNote && (
            <div className="patient-page-view">
              <div className="patient-records-header">
                <div className="records-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div>
                    <h3>Medical Record</h3>
                    <p>1 record available</p>
                  </div>
                </div>
              </div>

              <div className="patient-note-card open">
                <div className="note-summary">
                  <div className="note-summary-left">
                    <h4 className="note-title">{selectedPatient?.full_name || 'Demo Patient'}</h4>
                    <p className="note-meta">
                      {appointmentDate
                        ? new Date(appointmentDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Date unknown'}
                    </p>
                  </div>
                  <div className="note-summary-right">
                    <div className="note-tags">
                      {soapNote.pii_redacted && (
                        <span className="note-chip note-chip-success">PII Redacted</span>
                      )}
                      {soapNote.clinical_support && (
                        <span className="note-chip note-chip-info">AI Support</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="note-expand">
                  <div className="note-tabs">
                    <button
                      className={`note-tab ${activeTab === 'soap' ? 'active' : ''}`}
                      onClick={() => setActiveTab('soap')}
                    >
                      Full SOAP Note
                    </button>
                  </div>

                  {activeTab === 'soap' && soapNote?.soap_note && (
                    <div className="note-section glass">
                      {soapNote.soap_note.subjective && (
                        <div className="note-soap">
                          <div className="note-label">Subjective</div>
                          <div className="note-line"><FormattedText text={soapNote.soap_note.subjective} /></div>
                        </div>
                      )}
                      {soapNote.soap_note.objective && (
                        <div className="note-soap">
                          <div className="note-label">Objective</div>
                          <div className="note-line"><FormattedText text={soapNote.soap_note.objective} /></div>
                        </div>
                      )}
                      {soapNote.soap_note.assessment && (
                        <div className="note-soap">
                          <div className="note-label">Assessment</div>
                          <div className="note-line"><FormattedText text={soapNote.soap_note.assessment} /></div>
                        </div>
                      )}
                      {soapNote.soap_note.plan && (
                        <div className="note-soap">
                          <div className="note-label">Plan</div>
                          <div className="note-line"><FormattedText text={soapNote.soap_note.plan} /></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* SOAP Note Display - Default View */}
          {soapNote && !expandedNote && (
            <SOAPNoteDisplay
              soapNote={soapNote}
              editedSoapNote={soapNote.soap_note || {}}
              isEditing={false}
              copied={copied}
              isSaved={false}
              hasUnsavedChanges={false}
              isRegeneratingSupport={false}
              isSavingHistory={false}
              patientName={selectedPatient?.full_name || ''}
              appointmentDate={appointmentDate}
              fileName={selectedPatient?.audioFile.split('/').pop() || ''}
              audioFile={null}
              editingCodeIndex={null}
              editedCodeValue=""
              setEditedCodeValue={noOp}
              editedCodeDescription=""
              setEditedCodeDescription={noOp}
              editedCodeTiming=""
              setEditedCodeTiming={noOp}
              handleEditSOAP={handleEditSOAP}
              handleSaveEdit={noOp}
              handleCancelEdit={noOp}
              handleEditField={noOp}
              handleCloseSoapNote={handleCloseSoapNote}
              handleCopySOAP={handleCopySOAP}
              startEditingCode={noOp}
              saveEditedCode={noOp}
              cancelEditingCode={noOp}
              addNewCode={noOp}
              deleteCode={noOp}
              saveToHistory={saveToHistory}
              isDemoMode={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Demo;
